import { SVG_NS } from './part';
import { getState } from './state';
import { Wheel, WheelProps } from './wheel';
import { Scaler } from './scaler';
import {
  bindClearSpokesBtn,
  bindCopyUrlButton,
  bindHelpBtn,
  bindInputs,
  bindKeyboardShortcuts,
  bindPresetSelect,
  bindPrint,
  bindSelectResetting,
  bindSpinBtn,
  populateInputs,
  readQueryString,
} from './controls';
import { generateSpokeSummary } from './summary';

const getCleanCanvas = (): Element => {
  const svg = document.querySelector('svg.svg-container');
  if (!svg) {
    throw new Error('No SVG element found');
  }
  svg.innerHTML = '';
  const background = document.createElementNS(SVG_NS, 'rect');
  background.setAttribute('x', '0');
  background.setAttribute('y', '0');
  background.setAttribute('width', '100%');
  background.setAttribute('height', '100%');
  background.classList.add('background', 'select-resetting');
  svg.appendChild(background);
  return svg;
};

const createMessage = (message: string): SVGElement => {
  const text = document.createElementNS(SVG_NS, 'text');
  text.classList.add('message');
  text.setAttribute('x', '5px');
  text.setAttribute('y', '20px');
  text.textContent = message;
  return text;
};

const drawMessage = (message: string) => {
  const svg = getCleanCanvas();
  const text = createMessage(message);
  svg.appendChild(text);
};

const drawWheel = (width: number, height: number): Wheel | undefined => {
  const svg = getCleanCanvas();
  const wheel = getState().wheel;
  if (!wheel) {
    return;
  }
  const state = getState();
  const scaler = new Scaler(width, height, wheel.size);
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  const wheelElem = wheel.render({
    scaler,
    viewSide: state.viewParameters.ndsView.value ? 'nds' : 'ds',
    schematicView: !state.viewParameters.realisticScale.value,
    angleThreshold: state.viewParameters.angleThreshold.value,
  });
  wheelElem.setAttribute('transform', `translate(${width / 2} ${height / 2})`);
  svg.appendChild(wheelElem);
  const dirLabel = state.viewParameters.ndsView.value ? '[BIKE FRONT]' : '[BIKE BACK]';
  svg.appendChild(createMessage(dirLabel));
  return wheel;
};

const getWheelProps = (): WheelProps => {
  const params = getState().wheelParameters;
  return {
    rimErd: params.rimErd.value,
    rimNumHoles: params.rimNumHoles.value,
    rimDrillingOffsetDs: params.rimDrillingOffsetDs.value,
    rimDrillingOffsetNds: params.rimDrillingOffsetNds.value,
    rimFlipOffsetOrder: params.rimFlipOffsetOrder.value,
    dsFlangePcd: params.dsFlangePcd.value,
    ndsFlangePcd: params.ndsFlangePcd.value,
    dsFlangeOffset: params.dsFlangeOffset.value,
    ndsFlangeOffset: params.ndsFlangeOffset.value,
    dsFlangeNumHoles: params.dsFlangeNumHoles.value,
    ndsFlangeNumHoles: params.ndsFlangeNumHoles.value,
    hubRotation: params.hubRotation.value,
    dsNdsOffset: params.dsNdsOffset.value,
    spokeHoleDiameter: params.spokeHoleDiameter.value,
    spokeGroupingPrecision: params.spokeGroupingPrecision.value,
  };
};

const buildWheel = (spokeDefs: string | null) => {
  const wheel = new Wheel('WHEEL', getWheelProps());
  if (spokeDefs) {
    wheel.generateSpokesFromDefs(spokeDefs);
  }
  getState().wheel = wheel;
};

const refresh = (recalculate?: boolean) => {
  const svgBox = document.querySelector('svg.svg-container')?.getBoundingClientRect();
  if (!svgBox) {
    return;
  }
  if (Math.min(svgBox.width, svgBox.height) < 400) {
    drawMessage('Window too small!');
    return;
  }
  if (!!recalculate) {
    const state = getState();
    state.wheel?.update(getWheelProps());
    if (state.selectedId && state.wheel?.find(state.selectedId) === undefined) {
      state.select(undefined);
    }
  }
  const wheel = drawWheel(svgBox.width, svgBox.height);
  bindSelectResetting();
  if (wheel) {
    generateSpokeSummary(wheel.getSpokeGroups());
  }
};

window.addEventListener('resize', () => {
  refresh();
});
window.addEventListener('load', () => {
  const spokeDefs = readQueryString();
  populateInputs();
  bindInputs();
  bindClearSpokesBtn();
  bindKeyboardShortcuts();
  bindCopyUrlButton();
  bindPresetSelect();
  bindHelpBtn();
  bindSpinBtn();
  bindPrint();
  buildWheel(spokeDefs);
  getState().refresh = refresh;
  refresh();
});
